import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties } from "react";

/** Gap between the button and the popover, and the minimum distance from the viewport edges. */
const GAP = 8;
const EDGE = 16;
const MAX_WIDTH = 288;

export const LOGGED_IN_INFO_LABEL = "What does “Has logged in” mean?";

/**
 * Small circular "i" button explaining what "Has logged in" means (and that it is not "onboarded").
 * Click/tap toggles the popover; it also shows on hover and keyboard focus. Escape or a click
 * outside closes it. The popover is `position: fixed` and clamped to the viewport so it never
 * causes horizontal scroll and is not clipped by scrolling table wrappers.
 */
export default function LoggedInInfo({ className }: { className?: string }) {
  const popoverId = useId();
  const wrapRef = useRef<HTMLSpanElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLSpanElement>(null);
  const pinnedRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState<CSSProperties>({});

  const close = useCallback(() => {
    pinnedRef.current = false;
    setOpen(false);
  }, []);

  const position = useCallback(() => {
    const button = buttonRef.current;
    if (!button) return;
    const rect = button.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const width = Math.max(0, Math.min(MAX_WIDTH, viewportWidth - EDGE * 2));
    const centred = rect.left + rect.width / 2 - width / 2;
    const left = Math.max(EDGE, Math.min(centred, viewportWidth - EDGE - width));
    const height = popoverRef.current?.offsetHeight ?? 0;
    const below = rect.bottom + GAP;
    const top = below + height > viewportHeight && rect.top - GAP - height >= 0 ? rect.top - GAP - height : below;
    setStyle({ top, left, width });
  }, []);

  useLayoutEffect(() => {
    if (open) position();
  }, [open, position]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    const onPointerDown = (e: PointerEvent | MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) close();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown as unknown as EventListener);
    window.addEventListener("resize", position);
    window.addEventListener("scroll", position, true);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown as unknown as EventListener);
      window.removeEventListener("resize", position);
      window.removeEventListener("scroll", position, true);
    };
  }, [open, close, position]);

  return (
    <span
      ref={wrapRef}
      className={`directory-info${className ? ` ${className}` : ""}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => {
        if (!pinnedRef.current) setOpen(false);
      }}
    >
      <button
        ref={buttonRef}
        type="button"
        className="directory-info-btn"
        aria-label={LOGGED_IN_INFO_LABEL}
        aria-expanded={open}
        aria-controls={popoverId}
        aria-describedby={popoverId}
        onFocus={() => setOpen(true)}
        onBlur={close}
        onClick={() => {
          if (open && pinnedRef.current) {
            close();
          } else {
            pinnedRef.current = true;
            setOpen(true);
          }
        }}
      >
        <span aria-hidden>i</span>
      </button>
      <span
        ref={popoverRef}
        id={popoverId}
        role="tooltip"
        className="directory-info-popover"
        style={style}
        hidden={!open}
        // Keep focus on the button so clicking the popover text doesn't blur-close it.
        onMouseDown={(e) => e.preventDefault()}
      >
        <strong>Has logged in</strong>
        {" means the person has signed in to their Workvivo account at least once. It doesn't mean they've finished onboarding or have used Workvivo since then."}
      </span>
    </span>
  );
}
